import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faCompress, faSpinner, faTimes, faPalette, faMagic } from '@fortawesome/free-solid-svg-icons';
import './WebsitePreview.css';
import { getApiRootUrl } from '../../services/api';

interface WebsitePreviewProps {
    clientId: string;
    websiteUrl: string;
    htmlContent?: string;
    theme?: 'light' | 'dark';
    extractedBranding?: any;
    onClose?: () => void;
    onThemeClick?: () => void;
    hasThemeVariants?: boolean;
    isFetchingVariants?: boolean;
    isGeneratingVariants?: boolean;
    ecommerceAssistantEnabled?: boolean;
    onEcommerceAssistantToggle?: (enabled: boolean) => void;
    botType?: string | null;
}

export const WebsitePreview: React.FC<WebsitePreviewProps> = ({
    clientId,
    websiteUrl,
    htmlContent,
    theme = 'light',
    extractedBranding,
    onClose,
    onThemeClick,
    hasThemeVariants,
    isFetchingVariants = false,
    isGeneratingVariants = false,
    ecommerceAssistantEnabled = false,
    onEcommerceAssistantToggle,
    botType = null
}) => {
    void onEcommerceAssistantToggle;
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [iframeBlocked, setIframeBlocked] = useState(false);
    const iframeCheckTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [previewStrategy, setPreviewStrategy] = useState<'sanitized' | 'live' | 'proxy' | 'screenshot' | 'screenshot_unavailable'>('sanitized');
    const [rawSanitizedHtml, setRawSanitizedHtml] = useState<string | null>(null);
    const fallbackRequestRef = useRef<AbortController | null>(null);
    const [screenshotData, setScreenshotData] = useState<string | null>(null);
    const [screenshotMimeType, setScreenshotMimeType] = useState<string>('image/png');
    const [screenshotCapturedAt, setScreenshotCapturedAt] = useState<string | null>(null);
    const [screenshotReason, setScreenshotReason] = useState<string | null>(null);
    const [iframeCompatibilityChecked, setIframeCompatibilityChecked] = useState(false);
    const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);  // Thum.io screenshot URL
    const switchedToScreenshotRef = useRef(false);  // Track if we switched to screenshot mode
    const proxyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const sanitizedHtmlWithWidget = useMemo(() => {
        if (!rawSanitizedHtml) {
            return null;
        }

        const resolveWidgetLoaderUrl = () => {
            const normalizeOrigin = (value: string | undefined | null) => {
                if (!value || value === 'null' || value === 'undefined') {
                    return '';
                }
                return value.replace(/\/$/, '');
            };

            let origin = normalizeOrigin(
                typeof window !== 'undefined' ? window.location?.origin : ''
            );

            if (!origin && typeof window !== 'undefined') {
                try {
                    if (window.parent && window.parent !== window) {
                        origin = normalizeOrigin(window.parent.location?.origin);
                    }
                } catch (error) {
                    // Ignore cross-origin access errors; fall back to relative path.
                    origin = '';
                }
            }

            if (!origin) {
                return '/widget/widget-loader.js';
            }

            return `${origin}/widget/widget-loader.js`;
        };
        const widgetLoaderUrl = resolveWidgetLoaderUrl();
        const widgetScript = `
<script>
(function() {
    const CLIENT_ID = ${JSON.stringify(clientId)};
    const WIDGET_URL = ${JSON.stringify(widgetLoaderUrl)};
    const injectWidget = () => {
        const existingScript = document.querySelector('script[data-brndsh-widget="true"]');
        if (existingScript) {
            window.dispatchEvent(new Event('widget-theme-refresh'));
            return;
        }
        const widgetScriptEl = document.createElement('script');
        widgetScriptEl.src = WIDGET_URL;
        widgetScriptEl.async = true;
        widgetScriptEl.setAttribute('data-client-id', CLIENT_ID);
        widgetScriptEl.setAttribute('data-brndsh-widget', 'true');
        widgetScriptEl.onload = () => {
            setTimeout(() => window.dispatchEvent(new Event('widget-theme-refresh')), 1500);
        };
        document.body.appendChild(widgetScriptEl);
    };

    window.addEventListener('message', (event) => {
        if (!event || !event.data) return;
        if (event.data.type === 'refresh-widget' || event.data.type === 'refresh-widget-theme') {
            setTimeout(injectWidget, 300);
        }
    });

    window.addEventListener('widget-theme-refresh', () => {
        setTimeout(injectWidget, 300);
    });

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', injectWidget);
    } else {
        injectWidget();
    }
})();
</script>`;

        if (rawSanitizedHtml.match(/<\/body>/i)) {
            return rawSanitizedHtml.replace(/<\/body>/i, `${widgetScript}</body>`);
        }
        return `${rawSanitizedHtml}${widgetScript}`;
    }, [rawSanitizedHtml, clientId]);

    useEffect(() => {
        if (screenshotReason) {
            console.debug('[WebsitePreview] Screenshot unavailable reason:', screenshotReason);
        }
    }, [screenshotReason]);

    useEffect(() => {
        if (fallbackRequestRef.current) {
            fallbackRequestRef.current.abort();
            fallbackRequestRef.current = null;
        }

        setError(null);
        setIframeBlocked(false);
        setRawSanitizedHtml(null);
        setScreenshotData(null);
        setScreenshotCapturedAt(null);
        setScreenshotMimeType('image/png');
        setScreenshotReason(null);
        setIframeCompatibilityChecked(false);

        if (htmlContent) {
            setPreviewStrategy('sanitized');
            setRawSanitizedHtml(htmlContent);
            setIsLoading(true);
            return;
        }

        if (!clientId || !websiteUrl) {
            setPreviewStrategy('live');
            setIsLoading(false);
            return;
        }

        setPreviewStrategy('live');
        setIsLoading(true);
    }, [clientId, websiteUrl, htmlContent]);

    // Timeout fallback: If iframe doesn't load within 12 seconds, automatically try screenshot
    // This ensures we show something even if detection fails
    useEffect(() => {
        if (previewStrategy !== 'live' || !isLoading || iframeBlocked || screenshotData) {
            return;
        }

        const timeoutFallback = setTimeout(() => {
            // Double-check state before triggering fallback
            if (previewStrategy === 'live' && isLoading && !iframeBlocked && !screenshotData) {
                console.warn('[WebsitePreview] Timeout fallback: Iframe still loading after 12s, attempting screenshot fallback');
                setIframeBlocked(true);
            }
        }, 12000);

        return () => {
            clearTimeout(timeoutFallback);
        };
    }, [previewStrategy, isLoading, iframeBlocked, screenshotData]);

    // Listen for refresh messages from parent
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data.type === 'refresh-widget-theme' && event.data.clientId === clientId) {
                console.log('Received refresh-widget-theme message for client:', clientId, 'themeVariant:', event.data.themeVariant);

                if (iframeRef.current && !isLoading) {
                    // Send refresh message to the iframe
                    iframeRef.current.contentWindow?.postMessage({
                        type: 'refresh-widget-theme',
                        clientId: clientId,
                        themeVariant: event.data.themeVariant,
                        ecommerceAssistantEnabled: event.data.ecommerceAssistantEnabled
                    }, '*');

                    // Also dispatch refresh event
                    setTimeout(() => {
                        const previewDoc = iframeRef.current?.contentDocument;
                        if (previewDoc) {
                            const widgetContainer = previewDoc.querySelector('#ai-chat-widget-container');
                            if (widgetContainer && (widgetContainer as any).__reactInternalInstance) {
                                console.log('Found widget container, dispatching refresh event');
                                previewDoc.defaultView?.dispatchEvent(new Event('widget-theme-refresh'));
                            }
                        }
                    }, 500);
                }
            } else if (event.data.type === 'iframe-blocked' && previewStrategy === 'live') {
                console.log('[WebsitePreview] Received iframe-blocked message for:', event.data.url, 'reason:', event.data.reason);
                if (typeof event.data.reason === 'string' && !screenshotReason) {
                    setScreenshotReason(event.data.reason);
                }
                setIframeBlocked(true);
                setIsLoading(true);
                setError(null);
            } else if (event.data.type === 'site-loaded' && (previewStrategy === 'live' || previewStrategy === 'proxy')) {
                // Iframe loaded successfully - clear any pending timeout
                console.log('[WebsitePreview] Site loaded successfully via', previewStrategy);
                setIsLoading(false);
                setIframeBlocked(false);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [clientId, isLoading, previewStrategy, screenshotReason]);

    useEffect(() => {
        if (htmlContent) {
            return;
        }
        if (previewStrategy !== 'live') {
            return;
        }
        if (!websiteUrl) {
            return;
        }
        if (iframeCompatibilityChecked) {
            return;
        }

        const controller = new AbortController();
        let cancelled = false;

        const checkIframeCompatibility = async () => {
            try {
                const apiRoot = getApiRootUrl();
                const response = await fetch(
                    `${apiRoot}/api/demo-enhanced/iframe-support?url=${encodeURIComponent(websiteUrl)}`,
                    { signal: controller.signal }
                );

                if (!response.ok) {
                    return;
                }

                const payload = await response.json();
                if (cancelled) {
                    return;
                }

                if (payload?.blocked) {
                    if (!screenshotReason && Array.isArray(payload.reasons) && payload.reasons.length > 0) {
                        setScreenshotReason(payload.reasons[0]);
                    }
                    // Do not immediately mark iframe as blocked; allow live attempt first.
                    console.debug('[WebsitePreview] Iframe headers suggest blocking, proceeding to attempt load anyway.');
                }
            } catch (err) {
                if (!controller.signal.aborted) {
                    console.debug('Iframe compatibility check failed:', err);
                }
            } finally {
                if (!cancelled) {
                    setIframeCompatibilityChecked(true);
                }
            }
        };

        checkIframeCompatibility();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [previewStrategy, websiteUrl, htmlContent, iframeCompatibilityChecked, screenshotReason]);

    // When iframe is blocked, try proxy (no Playwright screenshot fallback)
    useEffect(() => {
        if (!iframeBlocked) {
            return;
        }
        if (previewStrategy !== 'live') {
            return;
        }
        if (!clientId || !websiteUrl) {
            return;
        }

        // Try proxy first - it's much faster than screenshot
        const apiRoot = getApiRootUrl();
        const widgetUrl = `${window.location.origin}/widget/widget-loader.js`;
        const proxyUrl = `${apiRoot}/api/demo-enhanced/iframe-proxy?url=${encodeURIComponent(websiteUrl)}&client_id=${encodeURIComponent(clientId)}&widget_url=${encodeURIComponent(widgetUrl)}`;

        console.log('[WebsitePreview] Iframe blocked detected, trying proxy fallback for:', websiteUrl);
        setPreviewStrategy('proxy');
        setIframeBlocked(false);
        setIsLoading(true);

        // Set iframe src to proxy URL
        if (iframeRef.current) {
            iframeRef.current.src = proxyUrl;
        }
    }, [iframeBlocked, previewStrategy, clientId, websiteUrl]);

    // Handle proxy failure - switch to screenshot mode with Microlink
    useEffect(() => {
        if (previewStrategy !== 'proxy') {
            return;
        }
        if (!clientId || !websiteUrl) {
            return;
        }

        // Set a timeout for proxy loading - if it fails, switch to screenshot mode
        proxyTimeoutRef.current = setTimeout(() => {
            if (previewStrategy === 'proxy' && isLoading && !switchedToScreenshotRef.current) {
                console.warn('[WebsitePreview] Proxy timeout, switching to screenshot mode');
                switchedToScreenshotRef.current = true;
                // Use WordPress mshots for screenshot (free, reliable, no auth required)
                const mShotsUrl = `https://s.wordpress.com/mshots/v1/${encodeURIComponent(websiteUrl)}?w=1920`;
                setScreenshotUrl(mShotsUrl);
                setPreviewStrategy('screenshot');
                setIsLoading(true);  // Keep loading while screenshot loads
            }
        }, 4000); // 4 second timeout for proxy

        return () => {
            if (proxyTimeoutRef.current) {
                clearTimeout(proxyTimeoutRef.current);
            }
        };
    }, [previewStrategy, isLoading, clientId, websiteUrl]);

    // Listen for proxy error messages (403 bot protection, etc.)
    useEffect(() => {
        const handleProxyError = (event: MessageEvent) => {
            // Check if it's an error from our proxy
            if (event.data?.type === 'proxy-error' && !switchedToScreenshotRef.current) {
                console.log('[WebsitePreview] Received proxy error, switching to screenshot mode:', event.data.error);
                if (proxyTimeoutRef.current) {
                    clearTimeout(proxyTimeoutRef.current);
                }
                switchedToScreenshotRef.current = true;
                // Use WordPress mshots for screenshot (free, reliable, no auth required)
                const mShotsUrl = `https://s.wordpress.com/mshots/v1/${encodeURIComponent(websiteUrl)}?w=1920`;
                setScreenshotUrl(mShotsUrl);
                setPreviewStrategy('screenshot');
                setIsLoading(true);  // Keep loading while screenshot loads
            }
        };

        window.addEventListener('message', handleProxyError);
        return () => window.removeEventListener('message', handleProxyError);
    }, [websiteUrl]);

    useEffect(() => {
        // If branding exists and iframe is already loaded, refresh the widget
        if (extractedBranding && iframeRef.current && !isLoading) {
            // Send a message to the iframe to refresh the widget
            try {
                iframeRef.current.contentWindow?.postMessage({
                    type: 'refresh-widget',
                    clientId: clientId
                }, '*');
                console.log('Sent refresh-widget message with extracted branding:', extractedBranding);

                // Also try to directly trigger refresh on the widget iframe within the preview
                setTimeout(() => {
                    const previewDoc = iframeRef.current?.contentDocument;
                    if (previewDoc) {
                        const widgetContainer = previewDoc.querySelector('#ai-chat-widget-container');
                        if (widgetContainer && (widgetContainer as any).__reactInternalInstance) {
                            console.log('Found widget container, dispatching refresh event');
                            previewDoc.defaultView?.dispatchEvent(new Event('widget-theme-refresh'));
                        }
                    }
                }, 1000);
            } catch (error) {
                console.error('Error sending message to iframe:', error);
            }
        }
    }, [extractedBranding, clientId, isLoading]);

    // Refresh iframe when e-commerce assistant toggle changes
    useEffect(() => {
        if (iframeRef.current && !isLoading) {
            console.log('E-commerce assistant toggle changed, sending refresh message to iframe');
            // Send refresh message to iframe to update theme settings
            iframeRef.current.contentWindow?.postMessage({
                type: 'refresh-widget-theme',
                clientId: clientId,
                themeVariant: extractedBranding,
                ecommerceAssistantEnabled: ecommerceAssistantEnabled
            }, '*');
        }
    }, [ecommerceAssistantEnabled, isLoading, clientId, extractedBranding]);

    // Inject widget directly for screenshot mode into the screenshot container
    useEffect(() => {
        // Always clean up screenshot widget first
        const cleanupWidget = () => {
            const script = document.querySelector('script[data-brndsh-screenshot-widget="true"]');
            if (script) {
                script.remove();
            }
            const widget = document.getElementById('ai-chat-widget-container');
            if (widget && widget.hasAttribute('data-screenshot-mode')) {
                widget.remove();
            }
        };

        cleanupWidget();

        // Only inject if we're in screenshot mode with screenshot data or URL
        if (previewStrategy === 'screenshot' && (screenshotData || screenshotUrl)) {
            // Wait for the screenshot container to be rendered
            const checkContainer = setInterval(() => {
                const container = document.getElementById('screenshot-widget-container');
                if (container) {
                    clearInterval(checkContainer);

                    // Create widget script with special data attribute
                    const widgetScript = document.createElement('script');
                    const origin = window.location.origin;
                    widgetScript.src = `${origin}/widget/widget-loader.js`;
                    widgetScript.async = true;
                    widgetScript.setAttribute('data-client-id', clientId);
                    widgetScript.setAttribute('data-brndsh-screenshot-widget', 'true');
                    widgetScript.setAttribute('data-screenshot-mode', 'true');

                    widgetScript.onload = () => {
                        console.log('[WebsitePreview] Widget loaded in screenshot mode');
                        // Mark any widget container created as screenshot mode
                        setTimeout(() => {
                            const widgetContainer = document.getElementById('ai-chat-widget-container');
                            if (widgetContainer) {
                                widgetContainer.setAttribute('data-screenshot-mode', 'true');
                                // Ensure widget is constrained to screenshot container and clickable
                                widgetContainer.style.position = 'absolute';
                                widgetContainer.style.zIndex = '999999';
                                widgetContainer.style.pointerEvents = 'auto';
                            }
                        }, 500);
                    };

                    // Inject script into the screenshot container instead of document.body
                    container.appendChild(widgetScript);
                    console.log('[WebsitePreview] Widget script injected into screenshot container');
                }
            }, 100);

            // Clear interval after 3 seconds if container not found
            setTimeout(() => clearInterval(checkContainer), 3000);
        }

        return () => {
            // Cleanup widget on unmount
            cleanupWidget();
        };
    }, [previewStrategy, screenshotData, screenshotUrl, clientId]);

    useEffect(() => {
        // Clear any existing timer
        if (iframeCheckTimerRef.current) {
            clearTimeout(iframeCheckTimerRef.current);
            iframeCheckTimerRef.current = null;
        }

        const iframe = iframeRef.current;
        if (!iframe) {
            return;
        }

        if (previewStrategy === 'screenshot' || previewStrategy === 'screenshot_unavailable') {
            if (previewStrategy === 'screenshot') {
                setIframeBlocked(false);
            }
            setError(null);
            setIsLoading(false);
            iframe.removeAttribute('src');
            iframe.removeAttribute('srcdoc');
            return;
        }

        // Handle proxy preview - iframe src is already set, just handle load/error
        if (previewStrategy === 'proxy') {
            const handleProxyLoad = () => {
                // Don't set loading to false if we already switched to screenshot mode
                // (the error postMessage arrives before onLoad)
                if (switchedToScreenshotRef.current) {
                    console.log('[WebsitePreview] Proxy onLoad fired but already switched to screenshot, ignoring');
                    return;
                }
                console.log('[WebsitePreview] Proxy iframe loaded successfully');
                setIsLoading(false);
                setError(null);
            };

            const handleProxyError = () => {
                if (switchedToScreenshotRef.current) {
                    return;
                }
                console.warn('[WebsitePreview] Proxy iframe failed to load, switching to screenshot');
                switchedToScreenshotRef.current = true;
                if (proxyTimeoutRef.current) {
                    clearTimeout(proxyTimeoutRef.current);
                }
                // Use WordPress mshots for screenshot (free, reliable, no auth required)
                const mShotsUrl = `https://s.wordpress.com/mshots/v1/${encodeURIComponent(websiteUrl)}?w=1920`;
                setScreenshotUrl(mShotsUrl);
                setPreviewStrategy('screenshot');
                setIsLoading(true);  // Keep loading while screenshot loads
            };

            iframe.onload = handleProxyLoad;
            iframe.onerror = handleProxyError;

            return () => {
                iframe.onload = null;
                iframe.onerror = null;
            };
        }

        // Handle sanitized preview path first
        if (previewStrategy === 'sanitized') {
            setIframeBlocked(false);
            setError(null);

            if (!sanitizedHtmlWithWidget) {
                setIsLoading(true);
                iframe.removeAttribute('src');
                iframe.removeAttribute('srcdoc');
                return;
            }

            setIsLoading(true);
            iframe.removeAttribute('src');
            iframe.srcdoc = sanitizedHtmlWithWidget;

            const handleLoad = () => {
                setIsLoading(false);
            };

            const handleError = () => {
                setIsLoading(false);
                setError('Failed to load preview');
            };

            iframe.onload = handleLoad;
            iframe.onerror = handleError;

            return () => {
                iframe.onload = null;
                iframe.onerror = null;
                iframe.removeAttribute('srcdoc');
            };
        }

        // Reset states for live fallback
        setIframeBlocked(false);
        setError(null);
        setIsLoading(true);

        // Create a simple HTML page that loads the client website and injects the widget
        const previewHtml = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <style>
                    body { margin: 0; padding: 0; }
                    iframe { width: 100%; height: 100vh; border: none; }
                </style>
            </head>
            <body>
                <iframe src="${websiteUrl}" id="site-frame" sandbox="allow-scripts allow-same-origin allow-forms allow-popups"></iframe>
                <script>
                    // Set up CSP violation detection IMMEDIATELY before any iframe operations
                    // This ensures we catch violations as soon as they occur
                    let blockNotified = false;
                    let cspCheckInterval = null;
                    let slowLoadTimer = null;
                    let loadCompleted = false;

                    function notifyBlocked(reason) {
                        if (blockNotified) {
                            return;
                        }
                        blockNotified = true;
                        
                        // Clear all timers
                        if (cspCheckInterval) {
                            clearInterval(cspCheckInterval);
                            cspCheckInterval = null;
                        }
                        if (slowLoadTimer) {
                            clearTimeout(slowLoadTimer);
                            slowLoadTimer = null;
                        }
                        
                        console.log('[PREVIEW] Iframe blocked detected:', reason);
                        window.parent.postMessage({ type: 'iframe-blocked', url: '${websiteUrl}', reason: reason }, '*');
                    }

                    // Listen for CSP violation events EARLY - before iframe is accessed
                    // This must be set up before the iframe src is set to catch violations immediately
                    document.addEventListener('securitypolicyviolation', (event) => {
                        console.log('[PREVIEW] CSP violation detected:', event.violatedDirective);
                        if (event.violatedDirective === 'frame-ancestors' || 
                            event.violatedDirective === 'frame-src') {
                            notifyBlocked('csp-violation');
                        }
                    }, { once: false });

                    // Now get the iframe element after event listeners are set up
                    const siteFrame = document.getElementById('site-frame');

                    // Helper function to check if message indicates CSP/iframe blocking
                    function isCSPBlockingMessage(text) {
                        const lowerText = text.toLowerCase();
                        // Check for specific "Framing ... violates" pattern
                        if (lowerText.includes('framing') && lowerText.includes('violates') && lowerText.includes('frame-ancestors')) {
                            return true;
                        }
                        // Check for other CSP/iframe blocking patterns
                        return lowerText.includes('refused to connect') ||
                            lowerText.includes('refused to frame') ||
                            lowerText.includes('x-frame-options') ||
                            lowerText.includes('frame-ancestors') ||
                            lowerText.includes('content security policy') ||
                            lowerText.includes('blocked a frame') ||
                            lowerText.includes('violates the following content security policy') ||
                            (lowerText.includes('violates') && lowerText.includes('directive'));
                    }

                    // Intercept console errors to catch "refused to connect" and CSP violation messages
                    const originalConsoleError = console.error;
                    console.error = function(...args) {
                        try {
                            const errorText = args.map(arg => String(arg)).join(' ');
                            if (isCSPBlockingMessage(errorText)) {
                                console.log('[PREVIEW] Console error detected - iframe likely blocked:', errorText.substring(0, 100));
                                notifyBlocked('console-error-detected');
                            }
                        } catch (e) {
                            // Ignore interception errors
                        }
                        if (typeof originalConsoleError === 'function') {
                            return originalConsoleError.apply(this, args);
                        }
                    };

                    // Intercept console warnings to catch CSP violations (often logged as warnings)
                    const originalConsoleWarn = console.warn;
                    console.warn = function(...args) {
                        try {
                            const warnText = args.map(arg => String(arg)).join(' ');
                            if (isCSPBlockingMessage(warnText)) {
                                console.log('[PREVIEW] Console warning detected - iframe likely blocked:', warnText.substring(0, 100));
                                notifyBlocked('console-warn-detected');
                            }
                        } catch (e) {
                            // Ignore interception errors
                        }
                        if (typeof originalConsoleWarn === 'function') {
                            return originalConsoleWarn.apply(this, args);
                        }
                    };

                    // Listen for window error events (catches CSP violations)
                    window.addEventListener('error', (event) => {
                        const message = event && typeof event.message === 'string' ? event.message : '';
                        if (isCSPBlockingMessage(message)) {
                            console.log('[PREVIEW] Window error detected - iframe blocked:', message.substring(0, 100));
                            notifyBlocked('window-error-csp');
                        }
                    }, { once: false });


                    // Track if iframe actually loads
                    siteFrame.onload = function() {
                        console.log('[PREVIEW] Site iframe onload fired');
                        loadCompleted = true;
                        if (slowLoadTimer) {
                            clearTimeout(slowLoadTimer);
                            slowLoadTimer = null;
                        }

                        // Check if it's an error page after load
                        setTimeout(() => {
                            if (blockNotified) return;
                            
                            try {
                                const doc = siteFrame.contentDocument;
                                if (doc && doc.body) {
                                    const bodyText = (doc.body.innerText || '').toLowerCase();
                                    if (bodyText.includes('refused to connect') ||
                                        bodyText.includes('err_blocked') ||
                                        bodyText.includes('this page cannot be displayed') ||
                                        bodyText.includes('blocked by response')) {
                                        console.log('[PREVIEW] Error page detected in iframe body');
                                        notifyBlocked('error-page-detected');
                                        return;
                                    }
                                }
                            } catch (e) {
                                // Can't access - could be cross-origin success or chrome-error page
                                // This is actually a good sign (cross-origin = loaded successfully)
                                console.log('[PREVIEW] Cannot access iframe after load - likely cross-origin (success)');
                            }
                        }, 300);
                    };

                    // Listen for error events on the iframe
                    siteFrame.onerror = function() {
                        console.log('[PREVIEW] Site iframe error event - likely blocked');
                        notifyBlocked('load-error');
                    };

                    // Slow load timeout - if iframe doesn't load within reasonable time, assume blocked
                    slowLoadTimer = setTimeout(() => {
                        if (!loadCompleted && !blockNotified) {
                            console.log('[PREVIEW] Iframe load timeout - likely blocked');
                            notifyBlocked('load-timeout');
                        }
                    }, 8000);

                    // CSP detection strategy: look for explicit browser error pages or missing load event
                    let cspCheckAttempts = 0;
                    const maxCspChecks = 8;
                    const cspCheckIntervalMs = 1000;
                    
                    cspCheckInterval = setInterval(() => {
                        if (blockNotified) {
                            clearInterval(cspCheckInterval);
                            return;
                        }
                        
                        cspCheckAttempts++;

                        try {
                            const iframeDoc = siteFrame.contentDocument;
                            if (iframeDoc && iframeDoc.body) {
                                const bodyText = (iframeDoc.body.innerText || '').toLowerCase();

                                if (bodyText.includes('refused to connect') ||
                                    bodyText.includes('err_blocked_by_response') ||
                                    bodyText.includes('this page cannot be displayed') ||
                                    bodyText.includes('blocked by response') ||
                                    bodyText.includes('content security policy')) {
                                    console.log('[PREVIEW] CSP Block detected: Browser error page found');
                                    notifyBlocked('csp-blocked-page');
                                    return;
                                }
                            }
                        } catch (e) {
                            // Cross-origin access denied; if the iframe eventually loads, this is fine
                            // Only log if we're still checking
                            if (cspCheckAttempts < maxCspChecks) {
                                console.log('[PREVIEW] CSP check', cspCheckAttempts, '- cannot access iframe (expected for cross-origin)');
                            }
                        }

                        if (cspCheckAttempts >= maxCspChecks) {
                            clearInterval(cspCheckInterval);
                            cspCheckInterval = null;
                            if (!loadCompleted && !blockNotified) {
                                console.log('[PREVIEW] CSP Block likely: iframe never finished loading after', maxCspChecks, 'checks');
                                notifyBlocked('csp-timeout');
                            }
                        }
                    }, cspCheckIntervalMs);

                    // REMOVED - This fallback was conflicting with the main detection

                    let widgetScript = null;
                    
                    // Function to inject or re-inject widget
                    function injectWidget() {
                        // Remove existing widget if present
                        const existingWidget = document.querySelector('#brndsh-widget-iframe');
                        if (existingWidget) {
                            existingWidget.remove();
                        }
                        
                        // Remove existing script
                        if (widgetScript) {
                            widgetScript.remove();
                        }
                        
                        // Create new script
                        widgetScript = document.createElement('script');
                        widgetScript.src = '${window.location.origin}/widget/widget-loader.js';
                        widgetScript.setAttribute('data-client-id', '${clientId}');
                        widgetScript.onload = function() {
                            // After widget loads, dispatch the theme refresh event
                            setTimeout(() => {
                                console.log('Dispatching widget-theme-refresh event');
                                window.dispatchEvent(new Event('widget-theme-refresh'));
                            }, 1500);
                        };
                        document.body.appendChild(widgetScript);
                        console.log('Widget injected/refreshed for client:', '${clientId}');
                    }
                    
                    // Listen for refresh messages
                    window.addEventListener('message', (event) => {
                        if (event.data.type === 'refresh-widget') {
                            console.log('Received refresh-widget message');
                            // Add a small delay to ensure theme is updated in backend
                            setTimeout(() => {
                                // Try multiple methods to refresh the widget theme
                                
                                // Method 1: Try to find the widget iframe and post message to it
                                const widgetFrame = document.querySelector('iframe[src*="widget"]');
                                if (widgetFrame) {
                                    console.log('Posting refresh-widget-theme message to widget iframe');
                                    widgetFrame.contentWindow.postMessage({ type: 'refresh-widget-theme' }, '*');
                                }
                                
                                // Method 2: Dispatch event on window
                                console.log('Dispatching widget-theme-refresh event');
                                window.dispatchEvent(new Event('widget-theme-refresh'));
                                
                                // Method 3: If all else fails, re-inject
                                setTimeout(() => {
                                    if (!window.refreshWidgetTheme && !widgetFrame) {
                                        console.log('No refresh method found, re-injecting widget');
                                        injectWidget();
                                    }
                                }, 500);
                            }, 1000);
                        }
                    });
                    
                    // Initial widget injection
                    setTimeout(() => {
                        injectWidget();
                    }, 2000);
                    
                    // If branding is available from the start, refresh after initial load
                    const hasBranding = ${extractedBranding ? 'true' : 'false'};
                    if (hasBranding) {
                        setTimeout(() => {
                            console.log('Refreshing widget with branding data');
                            // Dispatch theme refresh event instead of re-injecting
                            window.dispatchEvent(new Event('widget-theme-refresh'));
                        }, 4000);
                    }
                </script>
            </body>
            </html>
        `;

        if (iframeRef.current) {
            const iframe = iframeRef.current;
            const blob = new Blob([previewHtml], { type: 'text/html' });
            const blobUrl = URL.createObjectURL(blob);
            
            iframe.src = blobUrl;
            
            iframe.onload = () => {
                // Clean up blob URL after load
                URL.revokeObjectURL(blobUrl);
                // Detection is now handled entirely within the iframe script
            };

            iframe.onerror = () => {
                setIsLoading(false);
                setError('Failed to load preview');
                setIframeBlocked(true);
            };
        }

        // Cleanup timer on unmount
        return () => {
            if (iframeCheckTimerRef.current) {
                clearTimeout(iframeCheckTimerRef.current);
            }
        };
    }, [clientId, websiteUrl, previewStrategy, sanitizedHtmlWithWidget, extractedBranding]);

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const openInNewTab = () => {
        // For sites that block iframes, we have limited options
        // Best we can do is open the actual site and provide instructions

        // Open the actual website directly
        window.open(websiteUrl, '_blank');

        // Update the UI to show instructions
        setError('');
        setIframeBlocked(false);
        setIsLoading(false);

        // Optionally show a message
        return;
    };

    return (
        <div className={`website-preview-container ${isFullscreen ? 'fullscreen' : ''} ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
        }`}>
            {/* Header */}
            <div className={`preview-header flex-shrink-0 p-4 border-b ${
                theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
            }`}>
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className={`text-base font-semibold ${
                            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                        }`}>
                            Live Preview
                        </h3>
                        <p className={`text-xs mt-1 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                            How your AI widget will appear on {websiteUrl}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {onThemeClick && (
                            <button
                                onClick={onThemeClick}
                                className={`px-3 py-2 rounded-lg transition-all duration-300 flex items-center gap-2 ${
                                    hasThemeVariants
                                        ? theme === 'dark'
                                            ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                            : 'bg-purple-500 hover:bg-purple-600 text-white'
                                        : theme === 'dark'
                                            ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                                            : 'bg-gray-200 hover:bg-gray-300 text-gray-600'
                                }`}
                                title={hasThemeVariants ? "Show available theme variants" : "Generate theme variants from your brand colors"}
                                disabled={isFetchingVariants || isGeneratingVariants}
                            >
                                {isFetchingVariants || isGeneratingVariants ? (
                                    <FontAwesomeIcon icon={faSpinner} className="text-sm animate-spin" />
                                ) : (
                                    <FontAwesomeIcon icon={hasThemeVariants ? faPalette : faMagic} className="text-sm" />
                                )}
                                <span className="text-sm font-medium">
                                    {isFetchingVariants
                                        ? 'Loading...'
                                        : isGeneratingVariants
                                            ? 'Generating...'
                                            : hasThemeVariants
                                                ? 'Show Variants'
                                                : 'Create Variants'}
                                </span>
                            </button>
                        )}
                        {onClose && (
                            <button
                                onClick={onClose}
                                className={`p-2 rounded-lg transition-colors ${
                                    theme === 'dark' 
                                        ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-200' 
                                        : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                                }`}
                                title="Close preview"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                        <button
                            onClick={toggleFullscreen}
                            className={`p-2 rounded-lg transition-colors ${
                                theme === 'dark' 
                                    ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-200' 
                                    : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                            }`}
                            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                        >
                            <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Preview Content */}
            <div className="preview-content flex-1 relative">
                {isLoading && (
                    <div className={`absolute inset-0 flex items-center justify-center ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
                        <div className="text-center">
                            <div className="flex items-center justify-center mb-4">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="animate-spin">
                                    <circle cx="12" cy="12" r="3" stroke={theme === 'dark' ? '#9CA3AF' : '#6B7280'} strokeWidth="2"/>
                                    <path d="m12 1 0 6m0 10 0 6m11-7-6 0m-10 0-6 0m8.5-8.5 4.24-4.24m-12.02 12.02 4.24-4.24m8.48 0 4.24 4.24m-12.02-12.02 4.24 4.24" 
                                          stroke={theme === 'dark' ? '#9CA3AF' : '#6B7280'} strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                Loading your website preview...
                            </p>
                            <div className="flex items-center justify-center mt-3 space-x-1">
                                <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-400'} animate-pulse`}></span>
                                <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-400'} animate-pulse delay-75`}></span>
                                <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-400'} animate-pulse delay-150`}></span>
                            </div>
                        </div>
                    </div>
                )}
                
                {error && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className={`text-center ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
                            <p className="text-sm">{error}</p>
                        </div>
                    </div>
                )}

                {previewStrategy === 'screenshot_unavailable' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-opacity-95" style={{
                        backgroundColor: theme === 'dark' ? 'rgba(31, 41, 55, 0.95)' : 'rgba(249, 250, 251, 0.95)'
                    }}>
                        <div className={`text-center max-w-md p-8 rounded-lg ${
                            theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                        } shadow-lg`}>
                            <div className={`mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center ${
                                theme === 'dark' ? 'bg-yellow-900/20' : 'bg-yellow-100'
                            }`}>
                                <svg className={`w-8 h-8 ${theme === 'dark' ? 'text-yellow-500' : 'text-yellow-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h3 className={`text-lg font-semibold mb-2 ${
                                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                            }`}>
                                Preview Restricted
                            </h3>
                            <p className={`text-sm mb-4 ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                {websiteUrl} blocks embedding in iframes for security reasons.
                            </p>
                            <div className={`text-left mb-6 p-4 rounded-lg ${
                                theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
                            }`}>
                                <p className={`text-sm font-semibold mb-2 ${
                                    theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                                }`}>
                                    To see your widget on this site:
                                </p>
                                <ol className={`text-sm space-y-1 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    <li>1. Add the widget script to your website</li>
                                    <li>2. Visit your site directly to see it working</li>
                                </ol>
                            </div>
                            <button
                                onClick={openInNewTab}
                                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                                    theme === 'dark'
                                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                                } shadow-md hover:shadow-lg transform hover:-translate-y-0.5`}
                            >
                                Open Website →
                            </button>
                            <p className={`text-xs mt-4 ${
                                theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                            }`}>
                                This will open the actual website (without widget preview)
                            </p>
                        </div>
                    </div>
                )}

                {previewStrategy === 'screenshot' && (screenshotData || screenshotUrl) && (
                    <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 1 }}>
                        <div className="relative w-full h-full bg-gray-900">
                            <img
                                src={screenshotUrl || `data:${screenshotMimeType};base64,${screenshotData}`}
                                alt="Website screenshot preview"
                                className="w-full h-full object-cover object-top bg-white"
                                onLoad={(e) => {
                                    const img = e.currentTarget;
                                    // Check if image is broken (403 error pages often return tiny/empty images)
                                    if (img.naturalWidth < 100 || img.naturalHeight < 100) {
                                        console.log('[WebsitePreview] Screenshot appears broken (small dimensions), showing fallback');
                                        setIsLoading(false);
                                        setPreviewStrategy('screenshot_unavailable');
                                        setScreenshotReason('Screenshot could not be captured. Install the widget to see it on your live site.');
                                        return;
                                    }
                                    console.log('[WebsitePreview] Screenshot loaded:', img.naturalWidth, 'x', img.naturalHeight);
                                    setIsLoading(false);
                                }}
                                onError={() => {
                                    console.log('[WebsitePreview] Screenshot failed to load');
                                    setIsLoading(false);
                                    setPreviewStrategy('screenshot_unavailable');
                                    setScreenshotReason('Screenshot could not be loaded. Install the widget to see it on your live site.');
                                }}
                            />
                            <div className="absolute bottom-4 left-4 bg-black/60 text-white text-xs px-3 py-1 rounded-full backdrop-blur" style={{ zIndex: 2 }}>
                                {screenshotUrl
                                    ? '📸 Screenshot Preview - Widget is live!'
                                    : screenshotCapturedAt
                                        ? `Screenshot captured ${new Date(screenshotCapturedAt).toLocaleString()}`
                                        : 'Screenshot preview'}
                            </div>
                            {/* Widget overlay container - positioned as child of screenshot wrapper for proper containment */}
                            <div
                                id="screenshot-widget-container"
                                className="absolute inset-0"
                                style={{
                                    zIndex: 9999,
                                    pointerEvents: 'none',
                                    // Children can have pointer events, this just prevents the container from blocking
                                }}
                            >
                                {/* Widget will be injected here dynamically */}
                            </div>
                        </div>
                    </div>
                )}

                <iframe
                    ref={iframeRef}
                    className="w-full h-full border-0"
                    title="Website Preview"
                    style={{
                        display: previewStrategy === 'screenshot' ? 'none' : 'block',
                        backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff'
                    }}
                />
            </div>

            {/* Info Bar */}
            <div className={`preview-info flex-shrink-0 p-3 border-t text-xs ${
                theme === 'dark' ? 'bg-gray-900 border-gray-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-600'
            }`}>
                <div className="flex items-center gap-2">
                    <span>💡</span>
                    <span>
                        {previewStrategy === 'screenshot'
                            ? 'Showing a captured screenshot because this site blocks iframes. Your widget attaches to the live site once installed.'
                            : previewStrategy === 'screenshot_unavailable'
                                ? 'Preview screenshot isn’t available in this environment. Install the widget and open your site directly to experience it live.'
                            : 'The AI widget will appear in the bottom right corner of the preview'}
                    </span>
                </div>
            </div>
        </div>
    );
};
